import type { Notifier } from '@fleece/models';
import { Check, Plus, Send } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/settings/credential-dialogs';
import { NotifierDialog } from '@/components/settings/notifier-dialog';
import { RowActions, Rows } from '@/components/settings/table-parts';
import { Badge, Button, PageHeader, Spinner, Table, TableCard, Td, Th } from '@/components/ui';
import { useDeleteNotifier, useNotifiers, useTestNotifier } from '@/hooks/use-settings';

/** The notifier names the position monitor posts to unless told otherwise. */
const MONITOR_NOTIFIERS = ['monitor', 'attention'] as const;

type Dialog = { readonly kind: 'edit'; readonly editing?: Notifier; readonly suggestedName?: string } | { readonly kind: 'delete'; readonly notifier: Notifier };

/**
 * Where Fleece pushes what cannot wait: a Discord channel's webhook. The console is where
 * everything else is read; these are for the few things worth a notification.
 */
export function NotifiersPage() {
  const notifiers = useNotifiers();
  const [dialog, setDialog] = useState<Dialog | undefined>();
  const close = (): void => setDialog(undefined);
  const list = notifiers.data?.notifiers ?? [];
  const missing = notifiers.data === undefined ? [] : MONITOR_NOTIFIERS.filter((name) => !list.some((notifier) => notifier.name === name));

  return (
    <div className="mx-auto flex max-w-[1100px] flex-col gap-3 px-2 py-3 pb-6 sm:px-3">
      <PageHeader title="Notifiers" description="Where Fleece pushes what cannot wait. Everything else is read here, in the console." />

      <TableCard
        title="Discord notifiers"
        count={notifiers.data === undefined ? undefined : list.length}
        action={
          <Button variant="primary" onClick={() => setDialog({ kind: 'edit' })}>
            <Plus className="size-3.5" /> Add notifier
          </Button>
        }
        footer={
          missing.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-line px-2.5 py-2 text-xs text-muted">
              <span>The position monitor posts to “monitor” and “attention”.</span>
              {missing.map((name) => (
                <Button key={name} variant="outline" onClick={() => setDialog({ kind: 'edit', suggestedName: name })}>
                  <Plus className="size-3.5" /> Add “{name}”
                </Button>
              ))}
            </div>
          )
        }
      >
        <Rows query={notifiers} count={list.length} empty="No notifier yet. Add a Discord channel's webhook to be told when something needs you.">
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th className="hidden sm:table-cell">Channel</Th>
                <Th className="hidden md:table-cell">Webhook</Th>
                <Th>
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {list.map((notifier) => (
                <tr key={notifier.notifierId}>
                  <Td>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                        {notifier.name}
                        {MONITOR_NOTIFIERS.some((name) => name === notifier.name) && <Badge>Position monitor</Badge>}
                      </span>
                      {notifier.description !== undefined && <span className="text-[11px] text-muted">{notifier.description}</span>}
                      <span className="text-[11px] text-muted sm:hidden">{notifier.target.channel}</span>
                    </div>
                  </Td>
                  <Td className="hidden text-ink-2 sm:table-cell">{notifier.target.channel}</Td>
                  <Td className="hidden font-mono text-ink-2 md:table-cell">{notifier.target.webhookId}</Td>
                  <Td>
                    <RowActions name={notifier.name} onEdit={() => setDialog({ kind: 'edit', editing: notifier })} onDelete={() => setDialog({ kind: 'delete', notifier })}>
                      <TestButton notifier={notifier} />
                    </RowActions>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Rows>
      </TableCard>

      <NotifierDialog
        open={dialog?.kind === 'edit'}
        editing={dialog?.kind === 'edit' ? dialog.editing : undefined}
        suggestedName={dialog?.kind === 'edit' ? dialog.suggestedName : undefined}
        onClose={close}
      />
      <DeleteNotifierDialog notifier={dialog?.kind === 'delete' ? dialog.notifier : undefined} onClose={close} />
    </div>
  );
}

/** Posts a sample message; says it was sent, or Discord's refusal, on the row it was asked from. */
function TestButton({ notifier }: { notifier: Notifier }) {
  const test = useTestNotifier();
  return (
    <span className="flex items-center gap-1.5">
      {test.error !== null && (
        <span className="max-w-[16rem] text-right text-[11px] text-negative" role="alert">
          {test.error.message}
        </span>
      )}
      <Button variant="outline" onClick={() => test.mutate(notifier.notifierId)} disabled={test.isPending} title={`Send a test message to ${notifier.target.channel}`}>
        {test.isPending ? <Spinner /> : test.isSuccess ? <Check className="size-3.5 text-positive" /> : <Send className="size-3.5" />}
        <span className="max-sm:sr-only">{test.isSuccess ? 'Sent' : 'Send test'}</span>
      </Button>
    </span>
  );
}

function DeleteNotifierDialog({ notifier, onClose }: { notifier?: Notifier; onClose: () => void }) {
  const remove = useDeleteNotifier();
  const monitorUses = notifier !== undefined && MONITOR_NOTIFIERS.some((name) => name === notifier.name);
  return (
    <ConfirmDialog
      open={notifier !== undefined}
      title={notifier === undefined ? 'Delete' : `Delete ${notifier.name}?`}
      message={monitorUses ? 'The position monitor posts here, and its runs will fail until a notifier by this name is added again.' : 'Nothing more will be sent to this channel.'}
      serious={monitorUses}
      confirmLabel="Delete"
      busy={remove.isPending}
      error={remove.error?.message}
      onClose={() => {
        remove.reset();
        onClose();
      }}
      onConfirm={() => {
        if (notifier !== undefined) {
          remove.mutate(notifier.notifierId, { onSuccess: onClose });
        }
      }}
    />
  );
}
